// @ts-nocheck
import PropTypes from 'prop-types';
// material
import { alpha, styled } from '@mui/material/styles';
import { Box, Card, Stack, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

// ----------------------------------------------------------------------

// A premium split-screen brand panel (md+ only). A tinted, glassy surface with
// a soft brand glow, a short value-prop list and a framed product shot — it
// replaces the flat illustration card and gives the auth flow real presence
// without breaking the light page tone (so the dark-ink logo stays legible).
const SectionStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  flexShrink: 0,
  display: 'flex',
  overflow: 'hidden',
  position: 'relative',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  margin: theme.spacing(2, 0, 2, 2),
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
  boxShadow: theme.customShadows.z16,
  background: `radial-gradient(120% 80% at 85% 0%, ${alpha(
    theme.palette.primary.light,
    0.22
  )} 0%, ${alpha(theme.palette.primary.lighter, 0)} 55%), linear-gradient(180deg, ${alpha(
    theme.palette.primary.lighter,
    0.45
  )} 0%, ${theme.palette.background.paper} 60%)`
}));

const Eyebrow = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1.75),
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: theme.palette.primary.dark,
  background: alpha(theme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`
}));

// ----------------------------------------------------------------------

AuthBrandPanel.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.node,
  subtitle: PropTypes.string,
  bullets: PropTypes.arrayOf(PropTypes.string),
  image: PropTypes.string,
  imageAlt: PropTypes.string
};

export default function AuthBrandPanel({
  eyebrow = 'Workplace safety, done simple',
  title,
  subtitle,
  bullets = [],
  image,
  imageAlt = 'The WeSafe app'
}) {
  return (
    <SectionStyle>
      <Stack spacing={3} sx={{ p: 5, pt: 14 }}>
        <Eyebrow>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main' }} />
          {eyebrow}
        </Eyebrow>

        <Typography variant="h3" sx={{ color: 'text.primary', maxWidth: 360, textWrap: 'balance' }}>
          {title}
        </Typography>

        {subtitle && (
          <Typography sx={{ color: 'text.secondary', fontSize: 15, lineHeight: 1.7, maxWidth: 360 }}>
            {subtitle}
          </Typography>
        )}

        <Stack spacing={1.75} sx={{ mt: 1 }}>
          {bullets.map((item) => (
            <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  mt: '2px',
                  width: 22,
                  height: 22,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.dark',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.14)
                }}
              >
                <CheckRoundedIcon sx={{ fontSize: 14 }} />
              </Box>
              <Typography sx={{ color: 'text.primary', fontSize: 14.5, lineHeight: 1.5 }}>
                {item}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {image && (
        // Product illustration bleeding off the bottom edge for depth.
        <Box
          component="img" loading="lazy"
          src={image}
          alt={imageAlt}
          sx={{ width: '100%', display: 'block', mt: 2 }}
        />
      )}
    </SectionStyle>
  );
}
